import { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
    Table,
    Card,
    Spin,
    Button,
    Input,
    Select,
    Row,
    Col,
    DatePicker,
    message,
    Avatar,
} from "antd";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FileExcelOutlined, SearchOutlined, UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import EditOneEmployee from "../../components/EditOneEmployee";

const { Option } = Select;

export default function InactiveEmployees() {
    const { authData, backUrl } = useContext(AuthContext);
    const navigate = useNavigate();

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [showEmp, setShowEmp] = useState(false);

    const [pageSize, setPageSize] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [organizations, setOrganizations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [placesOfWork, setPlacesOfWork] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);
    const [selectedPlaceOfWork, setSelectedPlaceOfWork] = useState(null);
    const [selectedWorkLocation, setSelectedWorkLocation] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState("All");

    const [searchCode, setSearchCode] = useState("");
    const [searchName, setSearchName] = useState("");
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    // Build query params — accepts explicit overrides to avoid stale closure on filter
    const buildQueryParams = (overrides = {}, includePagination = true) => {
        const params = new URLSearchParams();
        params.append("is_active", "0");

        const _page = overrides.page ?? currentPage;
        const _pageSize = overrides.pageSize ?? pageSize;
        const _status = overrides.status ?? selectedStatus;
        const _code = overrides.searchCode ?? searchCode;
        const _name = overrides.searchName ?? searchName;
        const _org = overrides.selectedOrg ?? selectedOrg;
        const _dept = overrides.selectedDept ?? selectedDept;
        const _desig = overrides.selectedDesig ?? selectedDesig;
        const _pow = overrides.selectedPlaceOfWork ?? selectedPlaceOfWork;
        const _wl = overrides.selectedWorkLocation ?? selectedWorkLocation;
        const _from = overrides.fromDate ?? fromDate;
        const _to = overrides.toDate ?? toDate;

        if (includePagination) {
            params.append("page", _page);
            params.append("page_size", _pageSize);
        }
        if (_status && _status !== "All") params.append("status", _status);
        if (_code && _code.trim()) params.append("emp_code", _code.trim());
        if (_name && _name.trim()) params.append("employee_name", _name.trim());
        if (_org) params.append("organisation", _org);
        if (_dept) params.append("department", _dept);
        if (_desig) params.append("designation", _desig);
        if (_pow) params.append("place_of_work", _pow);
        if (_wl) params.append("location", _wl);
        if (_from) params.append("from_date", dayjs(_from).isValid() ? dayjs(_from).format("YYYY-MM-DD") : _from);
        if (_to) params.append("to_date", dayjs(_to).isValid() ? dayjs(_to).format("YYYY-MM-DD") : _to);

        return params.toString();
    };

    const fetchEmployees = async (overrides = {}) => {
        try {
            setLoading(true);
            const queryString = buildQueryParams(overrides, true);
            const res = await axios.get(
                `${backUrl}/api/emp/employees/?${queryString}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setEmployees(res.data.results || res.data);
            setTotalCount(res.data.count || res.data.length || 0);
        } catch (error) {
            console.error("Error fetching employees:", error);
            message.error("Failed to fetch employees");
        } finally {
            setLoading(false);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganizations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching organizations:", error);
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`);
            setDepartments(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching departments:", error);
            setDepartments([]);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`);
            setDesignations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching designations:", error);
            setDesignations([]);
        }
    };

    const fetchPlacesOfWork = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/worklocation/`);
            setPlacesOfWork(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching places of work:", error);
        }
    };

    useEffect(() => {
        fetchOrganizations();
        fetchPlacesOfWork();
        fetchEmployees();
    }, []);

    useEffect(() => {
        fetchEmployees();
    }, [currentPage, pageSize]);

    const renderValue = (value) =>
        value === null || value === undefined || value === "" ? "-" : value;

    // Format row for Excel export (essential fields only)
    const formatRow = (row) => ({
        "Employee Code": row.user?.username || "-",
        "Salutation": row.salutation || "-",
        "First Name": row.first_name || "-",
        "Surname": row.surname || "-",
        "Last Name": row.last_name || "-",
        "Organisation": row.organisation_name || "-",
        "Department": row.department_name || "-",
        "Designation": row.designation_name || "-",
        "Location": row.location || "-",
        "Place of Work": Array.isArray(row.place_of_work_names)
            ? row.place_of_work_names.join(", ")
            : row.place_of_work_names || "-",
        "Date of Joining": row.date_of_joining || "-",
        "Status": row.status || "-",
        "Mobile Number": row.mobile_number || "-",
        "Email": row.email_id || row.user?.email || "-",
        "Gender": row.gender || "-",
        "Blood Group": row.blood_group || "-",
        "Is HOD": row.is_hod ? "Yes" : "No",
        "Reporting To": row.reporting_to_name || "-",
        "Is Active": row.is_active ? "Yes" : "No",
    });

    const handleExportExcel = async () => {
        try {
            setExcelLoading(true);
            message.loading({ content: "Preparing Excel export...", key: "excel" });

            const queryString = buildQueryParams({}, false);
            const res = await axios.get(
                `${backUrl}/api/emp/employees/?${queryString}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            const allEmployees = res.data.results || res.data;
            if (!allEmployees || allEmployees.length === 0) {
                message.warning({ content: "No employees to export", key: "excel" });
                return;
            }

            const XLSX = require("xlsx");
            const formatted = allEmployees.map(formatRow);
            const ws = XLSX.utils.json_to_sheet(formatted);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Active Employees");
            XLSX.writeFile(wb, `ActiveEmployees_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.xlsx`);

            message.success({
                content: `Exported ${allEmployees.length} employees successfully!`,
                key: "excel",
            });
        } catch (error) {
            console.error("Error exporting to Excel:", error);
            message.error({ content: "Failed to export Excel file", key: "excel" });
        } finally {
            setExcelLoading(false);
        }
    };

    const handleFilter = () => {
        setCurrentPage(1);
        // Pass current filter values explicitly to avoid stale-closure issues
        fetchEmployees({
            page: 1,
            searchCode, searchName, selectedOrg, selectedDept, selectedDesig,
            selectedPlaceOfWork, selectedWorkLocation, status: selectedStatus,
            fromDate, toDate,
        });
    };

    const handleClearFilters = () => {
        const cleared = {
            page: 1, pageSize: 50,
            searchCode: "", searchName: "",
            selectedOrg: null, selectedDept: null, selectedDesig: null,
            selectedPlaceOfWork: null, selectedWorkLocation: null,
            status: "All", fromDate: null, toDate: null,
        };
        setSearchCode("");
        setSearchName("");
        setSelectedOrg(null);
        setSelectedDept(null);
        setSelectedDesig(null);
        setSelectedPlaceOfWork(null);
        setSelectedWorkLocation(null);
        setSelectedStatus("All");
        setFromDate(null);
        setToDate(null);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        setPageSize(50);
        fetchEmployees(cleared);
    };

    const columns = [
        {
            title: "Employee Code",
            dataIndex: ["user", "username"],
            key: "emp_code",
            fixed: "left",
            width: 180,
            render: (text, record) => (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar
                        size={40}
                        src={record.photo}
                        icon={!record.photo && <UserOutlined />}
                        style={{ backgroundColor: !record.photo ? "#1890ff" : undefined, flexShrink: 0 }}
                    />
                    <a
                        onClick={() => { setSelectedId(record.id); setShowEmp(true); }}
                        style={{ cursor: "pointer", color: "#1890ff", fontWeight: 500 }}
                    >
                        {record.emp_code || text || "-"}
                    </a>
                </div>
            ),
        },
        {
            title: "Name",
            key: "full_name",
            width: 180,
            render: (_, record) => {
                const parts = [record.salutation, record.first_name, record.surname, record.last_name]
                    .filter(Boolean);
                return parts.length ? parts.join(" ") : "-";
            },
        },
        {
            title: "Organisation",
            dataIndex: "organisation_name",
            key: "organisation_name",
            render: renderValue,
            width: 160,
        },
        {
            title: "Department",
            dataIndex: "department_name",
            key: "department_name",
            render: renderValue,
            width: 160,
        },
        {
            title: "Designation",
            dataIndex: "designation_name",
            key: "designation_name",
            render: renderValue,
            width: 160,
        },
        {
            title: "Location",
            dataIndex: "location",
            key: "location",
            render: renderValue,
            width: 130,
        },
        {
            title: "Place of Work",
            dataIndex: "place_of_work_names",
            key: "place_of_work",
            render: (v) =>
                Array.isArray(v) ? v.join(", ") || "-" : renderValue(v),
            width: 150,
        },
        {
            title: "Date of Joining",
            dataIndex: "date_of_joining",
            key: "date_of_joining",
            render: renderValue,
            width: 140,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (v) => {
                const colorMap = {
                    Confirmed: "#52c41a",
                    Probation: "#faad14",
                    Resigned: "#ff4d4f",
                };
                return (
                    <span
                        style={{
                            color: colorMap[v] || "#595959",
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 4,
                            background: `${colorMap[v] || "#595959"}18`,
                        }}
                    >
                        {v || "-"}
                    </span>
                );
            },
        },
        {
            title: "Mobile",
            dataIndex: "mobile_number",
            key: "mobile_number",
            render: renderValue,
            width: 130,
        },
        {
            title: "Email",
            dataIndex: "email_id",
            key: "email_id",
            render: (v, record) => renderValue(v || record.user?.email),
            width: 200,
        },
        {
            title: "Gender",
            dataIndex: "gender",
            key: "gender",
            render: renderValue,
            width: 90,
        },
        {
            title: "Blood Group",
            dataIndex: "blood_group",
            key: "blood_group",
            render: renderValue,
            width: 110,
        },
        {
            title: "Is HOD",
            dataIndex: "is_hod",
            key: "is_hod",
            render: (v) => (v ? "Yes" : "No"),
            width: 80,
        },
        {
            title: "Reporting To",
            dataIndex: "reporting_to_name",
            key: "reporting_to",
            render: renderValue,
            width: 160,
        },
        {
            title: "Status Changed Date",
            dataIndex: "status_changed_date",
            key: "status_changed_date",
            width: 170,
            render: (v) => v ? dayjs(v).format("DD MMM YYYY") : "-",
        },
    ];

    if (showEmp && selectedId) {
        return <EditOneEmployee employeeId={selectedId} onBack={() => setShowEmp(false)} />;
    }

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />

            <Card
                bordered
                style={{ marginBottom: 20, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
            >
                {/* Action Buttons */}
                <Row style={{ marginBottom: 12 }}>
                    <Col>
                        <Button
                            type="primary"
                            icon={<FileExcelOutlined />}
                            onClick={handleExportExcel}
                            loading={excelLoading}
                            style={{ marginRight: 10, background: "green", borderColor: "green" }}
                        />
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleFilter}
                            style={{ marginRight: 10, backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                        >
                            Filter
                        </Button>
                        <Button
                            onClick={handleClearFilters}
                            style={{ backgroundColor: "#f5f5f5", color: "#000" }}
                        >
                            Clear Filters
                        </Button>
                    </Col>
                </Row>

                {/* Row 1: Search Inputs + Org + Dept */}
                <Row gutter={16} style={{ marginBottom: 10 }}>
                    <Col span={6}>
                        <Input
                            allowClear
                            placeholder="Search Employee Code"
                            value={searchCode}
                            onChange={(e) => setSearchCode(e.target.value)}
                            onPressEnter={handleFilter}
                        />
                    </Col>
                    <Col span={6}>
                        <Input
                            allowClear
                            placeholder="Search Employee Name"
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            onPressEnter={handleFilter}
                        />
                    </Col>
                    <Col span={6}>
                        <Select
                            allowClear
                            showSearch
                            placeholder="Select Organization"
                            style={{ width: "100%" }}
                            value={selectedOrg}
                            onChange={(val) => {
                                setSelectedOrg(val);
                                setSelectedDept(null);
                                setSelectedDesig(null);
                                setDepartments([]);
                                setDesignations([]);
                                if (val) fetchDepartments(val);
                            }}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {organizations.map((o) => (
                                <Option key={o.id} value={o.id}>{o.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Select
                            showSearch
                            allowClear
                            placeholder="Select Department"
                            style={{ width: "100%" }}
                            value={selectedDept}
                            disabled={!selectedOrg}
                            onChange={(val) => {
                                setSelectedDept(val);
                                setSelectedDesig(null);
                                setDesignations([]);
                                if (val) fetchDesignations(val);
                            }}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {departments.map((d) => (
                                <Option key={d.id} value={d.id}>{d.name}</Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                {/* Row 2: Designation + Work Location + Place of Work + Status */}
                <Row gutter={16} style={{ marginBottom: 10 }}>
                    <Col span={6}>
                        <Select
                            showSearch
                            allowClear
                            placeholder="Select Designation"
                            style={{ width: "100%" }}
                            value={selectedDesig}
                            disabled={!selectedDept}
                            onChange={(val) => setSelectedDesig(val)}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {designations.map((d) => (
                                <Option key={d.id} value={d.id}>{d.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Select
                            allowClear
                            placeholder="Select Work Location"
                            style={{ width: "100%" }}
                            value={selectedWorkLocation}
                            onChange={(val) => setSelectedWorkLocation(val)}
                        >
                            <Option value="Head Office">Head Office</Option>
                            <Option value="Site Office">Site Office</Option>
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Select
                            showSearch
                            allowClear
                            placeholder="Select Place of Work"
                            style={{ width: "100%" }}
                            value={selectedPlaceOfWork}
                            onChange={(val) => setSelectedPlaceOfWork(val)}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {placesOfWork.map((p) => (
                                <Option key={p.id} value={p.id}>{p.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Select
                            allowClear
                            placeholder="Select Employment Status"
                            style={{ width: "100%" }}
                            value={selectedStatus}
                            onChange={setSelectedStatus}
                        >
                            <Option value="All">All</Option>
                            <Option value="Confirmed">Confirmed</Option>
                            <Option value="Probation">Probation</Option>
                            <Option value="Resigned">Resigned</Option>
                        </Select>
                    </Col>
                </Row>

                {/* Row 3: Date Range */}
                <Row gutter={16}>
                    <Col span={6}>
                        <DatePicker
                            style={{ width: "100%" }}
                            placeholder="From Date (Joining)"
                            value={fromDate ? dayjs(fromDate) : null}
                            onChange={(date, dateString) => setFromDate(dateString || null)}
                            allowClear
                            format="YYYY-MM-DD"
                        />
                    </Col>
                    <Col span={6}>
                        <DatePicker
                            style={{ width: "100%" }}
                            placeholder="To Date (Joining)"
                            value={toDate ? dayjs(toDate) : null}
                            onChange={(date, dateString) => setToDate(dateString || null)}
                            allowClear
                            format="YYYY-MM-DD"
                        />
                    </Col>
                </Row>
            </Card>

            {loading ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Table
                    rowKey="id"
                    dataSource={employees}
                    columns={columns}
                    bordered
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalCount,
                        showSizeChanger: true,
                        pageSizeOptions: ["8", "10", "20", "50", "100"],
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        },
                        onShowSizeChange: (_, size) => {
                            setPageSize(size);
                            setCurrentPage(1);
                        },
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    scroll={{ x: 2400 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}
        </>
    );
}